// src/components/Games/CaribbeanStud/CaribbeanStud.js
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  startCaribbeanStud,
  resolveCaribbeanStud,
  getCaribbeanStudHistory,
} from '../../../services/gameService';
import {
  Card,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
  InputGroup,
  FormControl,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaPlay,
  FaHistory,
  FaInfoCircle,
  FaHandRock,
  FaHandPaper,
  FaRedo,
  FaCrown,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './CaribbeanStud.css';

const CaribbeanStud = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Estado principal
  const [betAmount, setBetAmount] = useState(1);
  const [playerHand, setPlayerHand] = useState([]); // [{value,suit,card}]
  const [dealerUpcard, setDealerUpcard] = useState(null); // {value,suit,card}
  const [dealerHand, setDealerHand] = useState([]); // full dealer hand tras resolve
  const [dealerQualified, setDealerQualified] = useState(null); // bool tras resolve
  const [result, setResult] = useState(null); // 'win'|'lose'|'tie'|'fold'
  const [playerResultName, setPlayerResultName] = useState('');
  const [dealerResultName, setDealerResultName] = useState('');
  const [winnings, setWinnings] = useState(null);
  const [balance, setBalance] = useState(null);

  // Flags UI
  const [starting, setStarting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState('');
  const [showRules, setShowRules] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Derivados
  const inRound = useMemo(() => playerHand?.length > 0 && !result, [playerHand, result]);
  const roundResolved = useMemo(() => !!result, [result]);

  const getCardImage = (cardCode) =>
    `${process.env.PUBLIC_URL}/images/games/cards/${cardCode}.png`;

  const resetRound = () => {
    setPlayerHand([]);
    setDealerUpcard(null);
    setDealerHand([]);
    setDealerQualified(null);
    setResult(null);
    setPlayerResultName('');
    setDealerResultName('');
    setWinnings(null);
    setError('');
  };

  // === START (ANTE) ===
  const handleStart = async () => {
    setStarting(true);
    setError('');
    setResult(null);
    try {
      const data = await startCaribbeanStud(Number(betAmount));
      // Esperado: { playerHand, dealerUpcard, newBalance, canRaise }
      setPlayerHand(data.playerHand || []);
      setDealerUpcard(data.dealerUpcard || null);
      if (typeof data.newBalance === 'number') setBalance(data.newBalance);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        t('caribbean.error_start', { defaultValue: 'Error starting Caribbean Stud Poker' });
      setError(msg);
    } finally {
      setStarting(false);
    }
  };

  // === RESOLVE (FOLD / RAISE) ===
  const doResolve = async (action) => {
    setResolving(true);
    setError('');
    try {
      const data = await resolveCaribbeanStud(action); // { playerHand, dealerHand, dealerQualified, result, playerResult, dealerResult, winnings, newBalance }
      if (data.playerHand) setPlayerHand(data.playerHand);
      if (data.dealerHand) setDealerHand(data.dealerHand);
      if (typeof data.dealerQualified === 'boolean') setDealerQualified(data.dealerQualified);
      if (data.result) setResult(data.result);
      if (data.playerResult) setPlayerResultName(data.playerResult);
      if (data.dealerResult) setDealerResultName(data.dealerResult);
      if (typeof data.winnings === 'number') setWinnings(data.winnings);
      if (typeof data.newBalance === 'number') setBalance(data.newBalance);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        t('caribbean.error_resolve', { defaultValue: 'Error resolving Caribbean Stud Poker' });
      setError(msg);
    } finally {
      setResolving(false);
    }
  };

  // === HISTORY ===
  const openHistory = async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    setError('');
    try {
      const rows = await getCaribbeanStudHistory();
      setHistory(Array.isArray(rows) ? rows : []);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        t('caribbean.error_history', { defaultValue: 'Error fetching history' });
      setError(msg);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Icono de estado final
  const FinalBadge = () => {
    if (!result) return null;
    const color =
      result === 'win' ? 'success' : result === 'tie' ? 'warning' : result === 'fold' ? 'secondary' : 'danger';
    const text =
      result === 'win'
        ? t('caribbean.result.win', { defaultValue: 'You win!' })
        : result === 'tie'
        ? t('caribbean.result.tie', { defaultValue: 'Tie' })
        : result === 'fold'
        ? t('caribbean.result.fold', { defaultValue: 'Fold' })
        : t('caribbean.result.lose', { defaultValue: 'You lose' });

    return (
      <span className={`badge bg-${color}`} style={{ fontSize: '0.95rem' }}>
        {text}
      </span>
    );
  };

  return (
    <div className="carib-container d-flex justify-content-center align-items-start">
      <Card className="carib-card shadow-lg">
        <Card.Body>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Button
              variant="secondary"
              onClick={() => navigate('/sessions')}
              title={t('common.back', { defaultValue: 'Back' })}
            >
              <FaArrowLeft className="me-2" />
              {t('common.back', { defaultValue: 'Back' })}
            </Button>

            <h2 className="m-0">
              <FaCrown className="text-warning me-2" />
              {t('caribbean.title', { defaultValue: 'Caribbean Stud Poker' })}
              <FaCrown className="text-warning ms-2" />
            </h2>

            <div className="d-flex gap-2">
              <Button variant="outline-info" onClick={openHistory}>
                <FaHistory className="me-2" />
                {t('caribbean.history', { defaultValue: 'History' })}
              </Button>
              <Button variant="outline-secondary" onClick={() => setShowRules(true)}>
                <FaInfoCircle className="me-2" />
                {t('caribbean.rules', { defaultValue: 'Rules' })}
              </Button>
            </div>
          </div>

          {/* Apuesta + acciones */}
          <div className="carib-toolbar">
            <InputGroup style={{ maxWidth: 240 }}>
              <InputGroup.Text>{t('caribbean.bet', { defaultValue: 'Bet' })}</InputGroup.Text>
              <FormControl
                type="number"
                min={1}
                max={1_000_000}
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
              />
              <InputGroup.Text>{t('caribbean.currency', { defaultValue: 'MENEL' })}</InputGroup.Text>
            </InputGroup>

            <div className="d-flex gap-2">
              <Button onClick={handleStart} disabled={starting || inRound}>
                {starting ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    {t('caribbean.start', { defaultValue: 'Start' })}
                  </>
                )}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={resetRound}
                disabled={starting || (!inRound && !roundResolved)}
                title={t('caribbean.reset', { defaultValue: 'Reset' })}
              >
                <FaRedo className="me-2" />
                {t('caribbean.reset', { defaultValue: 'Reset' })}
              </Button>
            </div>

            <div className="carib-balance">
              {t('caribbean.balance', { defaultValue: 'Balance' })}:{' '}
              <strong>{balance ?? '—'}</strong> {t('caribbean.currency', { defaultValue: 'MENEL' })}
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}

          {/* Mano en curso */}
          {playerHand.length > 0 && (
            <>
              <Row className="mt-4">
                <Col md={7}>
                  <h5 className="mb-2">
                    {t('caribbean.player_hand', { defaultValue: 'Your hand' })}
                    {playerResultName && roundResolved ? ` — ${playerResultName}` : ''}
                  </h5>
                  <div className="carib-cards">
                    {playerHand.map((c, i) => (
                      <img
                        key={`${c.card}-${i}`}
                        className="carib-card-img"
                        src={getCardImage(c.card)}
                        alt={c.card}
                      />
                    ))}
                  </div>
                </Col>
                <Col md={5}>
                  <h5 className="mb-2">
                    {t('caribbean.dealer', { defaultValue: 'Dealer' })}{' '}
                    {roundResolved && dealerResultName ? `— ${dealerResultName}` : ''}
                  </h5>

                  {/* Antes de resolver, sólo upcard; luego toda la mano */}
                  {!roundResolved ? (
                    <div className="carib-cards">
                      {dealerUpcard ? (
                        <img
                          className="carib-card-img"
                          src={getCardImage(dealerUpcard.card)}
                          alt={dealerUpcard.card}
                        />
                      ) : null}
                      {/* 4 cartas ocultas como dorso */}
                      {[...Array(4)].map((_, idx) => (
                        <div key={idx} className="carib-card-back" aria-hidden />
                      ))}
                    </div>
                  ) : (
                    <div className="carib-cards">
                      {dealerHand.map((c, i) => (
                        <img
                          key={`${c.card}-${i}`}
                          className="carib-card-img"
                          src={getCardImage(c.card)}
                          alt={c.card}
                        />
                      ))}
                    </div>
                  )}
                  {roundResolved && (
                    <div className="mt-2 small">
                      {t('caribbean.dealer_qualified_label', { defaultValue: 'Dealer qualified' })}:{' '}
                      <strong>
                        {dealerQualified
                          ? t('caribbean.yes', { defaultValue: 'Yes' })
                          : t('caribbean.no', { defaultValue: 'No' })}
                      </strong>
                    </div>
                  )}
                </Col>
              </Row>

              {/* Acciones Raise/Fold mientras la ronda está viva */}
              {!roundResolved && (
                <div className="d-flex justify-content-center gap-3 mt-4">
                  <Button
                    variant="success"
                    onClick={() => doResolve('raise')}
                    disabled={resolving}
                    title={t('caribbean.raise', { defaultValue: 'Raise' })}
                  >
                    {resolving ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <FaHandRock className="me-2" />
                        {t('caribbean.raise', { defaultValue: 'Raise' })}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={() => doResolve('fold')}
                    disabled={resolving}
                    title={t('caribbean.fold', { defaultValue: 'Fold' })}
                  >
                    <FaHandPaper className="me-2" />
                    {t('caribbean.fold', { defaultValue: 'Fold' })}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Resultado final */}
          {roundResolved && (
            <div className="mt-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <FinalBadge />
              </div>
              <div>
                {t('caribbean.winnings', { defaultValue: 'Winnings' })}:{' '}
                <strong>{typeof winnings === 'number' ? winnings : 0}</strong>{' '}
                {t('caribbean.currency', { defaultValue: 'MENEL' })}
              </div>
              <div>
                {t('caribbean.new_balance', { defaultValue: 'New balance' })}:{' '}
                <strong>{balance ?? '—'}</strong> {t('caribbean.currency', { defaultValue: 'MENEL' })}
              </div>

              <div className="d-flex gap-2 mt-3">
                <Button onClick={handleStart} disabled={starting || inRound}>
                  {starting ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <FaPlay className="me-2" />
                      {t('caribbean.play_again', { defaultValue: 'Play again' })}
                    </>
                  )}
                </Button>
                <Button variant="outline-secondary" onClick={resetRound}>
                  <FaRedo className="me-2" />
                  {t('caribbean.reset', { defaultValue: 'Reset' })}
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal: Reglas */}
      <Modal show={showRules} onHide={() => setShowRules(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaInfoCircle className="me-2" />
            {t('caribbean.rules', { defaultValue: 'Rules' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ul className="mb-0">
            <li>
              <strong>{t('caribbean.rule_ante', { defaultValue: 'Ante' })}:</strong>{' '}
              {t('caribbean.rule_ante_text', {
                defaultValue: 'You pay the Ante to receive 5 cards. Dealer shows one card face up.',
              })}
            </li>
            <li>
              <strong>{t('caribbean.rule_decision', { defaultValue: 'Decision' })}:</strong>{' '}
              {t('caribbean.rule_decision_text', {
                defaultValue:
                  'Choose Raise (2×Ante) to continue, or Fold to forfeit your Ante.',
              })}
            </li>
            <li>
              <strong>{t('caribbean.rule_qualify', { defaultValue: 'Dealer qualifies' })}:</strong>{' '}
              {t('caribbean.rule_qualify_text', {
                defaultValue:
                  'Dealer qualifies with A-K high or any pair or better.',
              })}
            </li>
            <li>
              <strong>{t('caribbean.rule_payouts', { defaultValue: 'Payouts' })}:</strong>{' '}
              {t('caribbean.rule_payouts_text', {
                defaultValue:
                  'If dealer does not qualify: Ante 1:1, Raise pushes. If dealer qualifies and you win: Ante 1:1 + Raise paid by paytable. Tie: push.',
              })}
            </li>
          </ul>
        </Modal.Body>
      </Modal>

      {/* Modal: Historial */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHistory className="me-2" />
            {t('caribbean.history', { defaultValue: 'History' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 420, overflowY: 'auto' }}>
          {historyLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner />
            </div>
          ) : history.length ? (
            <ul className="list-group">
              {history.map((h, idx) => (
                <li key={idx} className="list-group-item">
                  <div>
                    <strong>{t('caribbean.result_label', { defaultValue: 'Result' })}:</strong>{' '}
                    {h.result}
                  </div>
                  {'score' in h && (
                    <div>
                      <strong>{t('caribbean.score', { defaultValue: 'Score' })}:</strong> {h.score}
                    </div>
                  )}
                  <div>
                    <strong>{t('caribbean.details', { defaultValue: 'Details' })}:</strong> {h.details}
                  </div>
                  <div>
                    <strong>{t('caribbean.date', { defaultValue: 'Date' })}:</strong>{' '}
                    {new Date(h.created_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-muted">
              {t('caribbean.no_history', { defaultValue: 'No records yet.' })}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CaribbeanStud;
